import * as React from 'react';
import clsx from 'clsx';
import { Card, Typography, FormControlLabel, Switch, IconButton } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { useFeatureLayoutCardStyles } from './FeatureLayoutCardDraggable';

interface FeatureLayoutCardCloneProps {
    featureGuid: string;
    feature: string;
    title: string;
    isHidden: boolean;
    onIsHiddenUpdated?: (featureGuid: string, isHidden: boolean) => void;
    /** The real card's measured width (px) - the clone is portalled out of the grid while
     * dragging, so it can't inherit its column width from CSS. Null before a drag starts, or
     * if the source node couldn't be found - falls back to 100% rather than collapsing. */
    width: number | null;
    infoUrl: string | null;
}

export const FeatureLayoutCardClone: React.FC<FeatureLayoutCardCloneProps> = props => {
    const classes = useFeatureLayoutCardStyles();

    const [isVisible, setIsVisible] = React.useState<boolean>(!props.isHidden);

    const onHiddenChange = () => {
        setIsVisible(!isVisible);

        if (props.onIsHiddenUpdated) {
            props.onIsHiddenUpdated(props.featureGuid, !isVisible);
        }
    };

    const cardWidth = props.width !== null ? `${props.width}px` : '100%';

    return (
        <div
            className={classes.cardSlot}
            style={{ minWidth: cardWidth, maxWidth: cardWidth }}
        >
            <Card className={clsx(classes.card, !isVisible && classes.cardHidden)}>
                <div className={classes.cardBody}>
                    <div className={classes.iconChip}>
                        <TrendingUpIcon />
                    </div>
                    <Typography className={classes.title}>
                        {props.title}
                    </Typography>
                    <DragIndicatorIcon className={classes.dragIndicator} />
                </div>
                <div className={classes.cardFooter}>
                    <FormControlLabel
                        className={classes.visibilityLabel}
                        control={
                            <Switch
                                size="small"
                                checked={isVisible}
                                onChange={onHiddenChange}
                                color="primary"
                            />
                        }
                        label={isVisible ? 'Visible' : 'Hidden'}
                    />
                    <div className={classes.footerActions}>
                        {props.infoUrl && (
                            <IconButton
                                aria-label="learn more"
                                size="small"
                                className={classes.footerButton}
                            >
                                <VideoLibraryOutlinedIcon />
                            </IconButton>
                        )}
                        <IconButton
                            aria-label="view notes"
                            size="small"
                            className={classes.footerButton}
                        >
                            <InfoIcon key={props.feature} />
                        </IconButton>
                    </div>
                </div>
            </Card>
        </div>
    );
};
